const prefix = 'mesomb.idempotency.';
const records = new Map();
const cooldown = 2000;
const digest = async value => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', value)), byte => byte.toString(16).padStart(2,'0')).join('');
const hash = value => digest(new TextEncoder().encode(value));

export async function canonical(value) {
  if (value === undefined) return ['undefined'];
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return ['date',value.toISOString()];
  if (value instanceof Blob) return ['blob',value.type,value instanceof File ? value.name : '',await digest(await value.arrayBuffer())];
  if (value instanceof ArrayBuffer) return ['bytes',await digest(value)];
  if (ArrayBuffer.isView(value)) return ['bytes',await digest(value.buffer.slice(value.byteOffset,value.byteOffset+value.byteLength))];
  if (value instanceof FormData || value instanceof URLSearchParams) {
    const entries = await Promise.all(Array.from(value.entries(), async ([key,item]) => [key,await canonical(item)]));
    // Stable sort preserves the order of repeated fields.
    entries.sort((a,b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0);
    return [value instanceof FormData ? 'form' : 'query',entries];
  }
  if (Array.isArray(value)) return ['array',await Promise.all(value.map(canonical))];
  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return ['object',await Promise.all(Object.keys(value).sort().map(async key => [key,await canonical(value[key])]))];
  }
  throw new TypeError('Unsupported request body for automatic idempotency; provide an explicit operation key.');
}

function read(id) {
  let value;
  try {value = localStorage.getItem(prefix+id);} catch { /* Memory fallback. */ }
  if (!value) {try {value = sessionStorage.getItem(prefix+id);} catch { /* Memory fallback. */ }}
  if (value) {
    let record;
    try { record = JSON.parse(value); } catch { throw new Error('Stored operation key is invalid. Check the transaction status before retrying.'); }
    if (!record || !/^[A-Za-z0-9_.:-]{1,128}$/.test(record.key) || !/^[a-f0-9]{64}$/.test(record.bodyHash)) {
      throw new Error('Stored operation key is invalid. Check the transaction status before retrying.');
    }
    return record;
  }
  return records.get(id);
}
function write(id, record, required = true) {
  records.set(id,record);
  let shared = false;
  try {localStorage.setItem(prefix+id,JSON.stringify(record)); shared = true;} catch { /* Session fallback. */ }
  if (!shared && required) {
    records.delete(id);
    throw new Error('Cannot safely save the operation key. Enable browser storage or use an explicit operation key.');
  }
}
async function locked(id, action) {
  if (globalThis.navigator?.locks) return navigator.locks.request(prefix+id,action);
  // Fail closed across unsupported browsers rather than claim cross-tab atomicity.
  throw new Error('Web Locks are required for automatic idempotency. Use a supported browser or an explicit operation key.');
}
export async function prepareIdempotency(config, scope) {
  if (!['post','put','patch','delete'].includes(config.method?.toLowerCase())) return;
  const explicit = config.headers.get('Idempotency-Key');
  if (explicit) return;
  const bodyHash = await hash(JSON.stringify(await canonical(config.data)));
  const intent = config.mesombOperationId;
  const id = await hash(JSON.stringify(await canonical({
    method:config.method.toLowerCase(), url:new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(config.url) ? config.url : `${config.baseURL.replace(/\/+$/, '')}/${config.url.replace(/^\/+/, '')}`).href,
    params:config.params, scope, intent:intent ?? bodyHash,
  })));
  // Recover keys created before Axios URL resolution was aligned with transport.
  const previousId = await hash(JSON.stringify(await canonical({
    method:config.method.toLowerCase(), url:new URL(config.url,config.baseURL).href,
    params:config.params, scope, intent:intent ?? bodyHash,
  })));
  const legacy = await hash(JSON.stringify({method:config.method,url:config.url,data:config.data,params:config.params,
    environment:scope.environment,application:scope.application,user:scope.user === 'public' ? undefined : scope.user}));
  await locked(id, () => {
    if (config.signal?.aborted) throw new Error('Session changed');
    let record = read(id);
    if (!record && previousId !== id) record = read(previousId);
    if (!record) {
      try {
        const previous = sessionStorage.getItem(prefix+legacy);
        if (previous && /^[A-Za-z0-9_.:-]{1,128}$/.test(previous)) {
          record = {key:previous,bodyHash};
          write(id,record);
          sessionStorage.removeItem(prefix+legacy);
        }
      } catch { /* Storage may be blocked. */ }
    }
    if (!intent && record?.finishedAt && Date.now()-record.finishedAt >= cooldown) record = undefined;
    if (record && record.bodyHash !== bodyHash) throw new Error('This operation has unresolved payment details. Check its status before changing the request.');
    record ??= {key:crypto.randomUUID(),bodyHash};
    write(id,record);
    config.headers.set('Idempotency-Key',record.key);
    config.mesombIdempotencyFingerprint = id;
  });
}
export async function settleIdempotency(config, response) {
  const id = config?.mesombIdempotencyFingerprint;
  if (!id || !response) return;
  if (isUncertainResponse(response)) return;
  await locked(id, () => {
    const record = read(id);
    // A late response cannot overwrite a newer intentional operation.
    if (record?.key === config.headers.get('Idempotency-Key')) write(id,{...record,finishedAt:Date.now()},false);
  });
}
export function clearIdempotencyMemory() {records.clear();}

export function isUncertainResponse(response) {
  const codes = [response.data?.code,response.data?.error?.code];
  return response.status >= 500 || [408,409,425,429].includes(response.status) ||
    codes.includes('request_in_progress') || codes.includes('idempotency_key_conflict') ||
    response.data?.status === 'active';
}
